import type { DocumentVerwijzing } from '@eformulieren/model'

/**
 * De verwijzing van een bestand, uitgerekend in de browser.
 *
 * Content-addressed betekent dat de hash het adres is (U25, R-199). Die hash kan de
 * browser zelf uitrekenen met `crypto.subtle`, dus de control levert een geldige
 * `DocumentVerwijzing` zonder dat er een server aan te pas komt. Wat in M2 ontbreekt
 * is alleen het wegschrijven van de bytes; dat is de opslag van M3.
 *
 * De volledige SHA-256 en geen afkapping: een afgekapte hash is niet
 * content-addressed. Het model weigert hem ook (`sha256:` plus 64 hextekens).
 */
export async function fileReferenceOf(file: File): Promise<DocumentVerwijzing> {
  const bytes = await file.arrayBuffer()
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  const hex = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
  return {
    hash: `sha256:${hex}`,
    // De naam hoort bij de verwijzing en niet bij het bestand: twee mensen kunnen
    // hetzelfde bestand onder een andere naam aanhangen (model, `DocumentVerwijzing`).
    bestandsnaam: file.name,
    grootteBytes: file.size,
  }
}

/** De extensie van een bestandsnaam, zonder punt en in kleine letters. */
export function extension(fileName: string): string | undefined {
  const point = fileName.lastIndexOf('.')
  if (point <= 0 || point === fileName.length - 1) return undefined
  return fileName.slice(point + 1).toLowerCase()
}
